const API_URL = process.env.REACT_APP_API_URL;

const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    // Compatibilidade com endpoints que leem esse header customizado
    headers['Authentication'] = `Bearer ${token}`;
  }

  return headers;
};

export interface FleetListingFilters {
  dealerNetworkId?: string;
  dealerId?: string;
  dealerNetworkCnpj?: string;
  dealerCnpj?: string;
  start?: string;
  end?: string;
  status?: string;
  situacao?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface FleetListingItem {
  id: string;
  grupo: string;
  cnpjConcessionaria: string;
  razaoSocialConcessionaria: string;
  placa: string;
  chassi: string;
  valor: number;
  valorPremioAcumulado: number;
  data: string;
  status: string;
  situacao: string;
}

export interface FleetListingPageResponse {
  content: FleetListingItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface VehiclesReportParams {
  period: string;
  start?: string;
  end?: string;
  dealerNetworkId?: string;
  dealerId?: string;
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

// Converte para YYYY-MM-DD (sem hora), compatível com filtros de listagem.
const normalizeDateFilter = (value?: string): string | undefined => {
  if (!value || !value.trim()) return undefined;

  const raw = value.trim();
  // Se já vier no formato YYYY-MM-DD, mantém
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const [yearStr, monthStr, dayStr] = raw.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (!year || !month || !day) return undefined;

  return `${year}-${pad2(month)}-${pad2(day)}`;
};

const extractBackendErrorMessage = async (
  response: Response,
  fallbackMessage: string
): Promise<string> => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data && typeof data === 'object' && typeof (data as any).message === 'string') {
        return (data as any).message;
      }
    } else {
      const text = await response.text();
      if (text && text.trim()) return text;
    }
  } catch {
    // fallback
  }
  return fallbackMessage;
};

export const fleetListingService = {
  async getFleetListing(filters: FleetListingFilters = {}): Promise<FleetListingPageResponse> {
    const query = new URLSearchParams();

    if (filters.dealerNetworkId?.trim()) query.set('dealerNetworkId', filters.dealerNetworkId.trim());
    if (filters.dealerId?.trim()) query.set('dealerId', filters.dealerId.trim());
    if (filters.dealerNetworkCnpj?.trim()) query.set('dealerNetworkCnpj', filters.dealerNetworkCnpj.trim());
    if (filters.dealerCnpj?.trim()) query.set('dealerCnpj', filters.dealerCnpj.trim());

    const normalizedStart = normalizeDateFilter(filters.start);
    const normalizedEnd = normalizeDateFilter(filters.end);
    if (normalizedStart) query.set('start', normalizedStart);
    if (normalizedEnd) query.set('end', normalizedEnd);

    if (filters.status?.trim()) query.set('status', filters.status.trim());
    if (filters.situacao?.trim()) query.set('situacao', filters.situacao.trim());
    query.set('page', String(filters.page ?? 0));
    query.set('size', String(filters.size ?? 5));
    if (filters.sort?.trim()) {
      query.set('sort', filters.sort.trim());
    } else {
      query.set('sort', 'createdAt,desc');
    }

    const url = `${API_URL}/api/v1/contracts/fleet-listing${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível carregar a listagem de frotas. Por favor, tente novamente.');
      throw new Error(message);
    }

    const data = await response.json();

    // Fallback defensivo caso backend ainda retorne array em algum ambiente.
    if (Array.isArray(data)) {
      return {
        content: data,
        totalElements: data.length,
        totalPages: data.length > 0 ? 1 : 0,
        size: filters.size ?? 5,
        number: filters.page ?? 0,
        first: true,
        last: true
      };
    }

    return {
      content: Array.isArray(data?.content) ? data.content : [],
      totalElements: Number(data?.totalElements ?? 0),
      totalPages: Number(data?.totalPages ?? 0),
      size: Number(data?.size ?? (filters.size ?? 5)),
      number: Number(data?.number ?? (filters.page ?? 0)),
      first: Boolean(data?.first),
      last: Boolean(data?.last)
    };
  },

  async downloadVehiclesReport(params: VehiclesReportParams): Promise<void> {
    const query = new URLSearchParams();
    query.set('period', params.period);

    if (params.start?.trim()) query.set('start', params.start.trim());
    if (params.end?.trim()) query.set('end', params.end.trim());
    if (params.dealerNetworkId?.trim()) query.set('dealerNetworkId', params.dealerNetworkId.trim());
    if (params.dealerId?.trim()) query.set('dealerId', params.dealerId.trim());

    const url = `${API_URL}/api/v1/contracts/vehicles-report?${query.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível gerar o relatório. Verifique os filtros e tente novamente.');
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `relatorio-veiculos-${new Date().toISOString().slice(0, 10)}.xlsx`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },

  async updateBillingStatus(id: string, targetStatus: string): Promise<void> {
    const query = new URLSearchParams({ status: targetStatus });
    const url = `${API_URL}/api/v1/contracts/${id}/update-billing-status?${query.toString()}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível atualizar o status. Por favor, tente novamente.');
      throw new Error(message);
    }
  },

  async updateBillingStatusBulk(ids: string[], targetStatus: string): Promise<void> {
    const query = new URLSearchParams({ status: targetStatus });
    const url = `${API_URL}/api/v1/contracts/update-billing-status?${query.toString()}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(ids)
    });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível atualizar os registros em lote. Por favor, tente novamente.');
      throw new Error(message);
    }
  }
};
